import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag, Check, Flame, Leaf, Beef, ChefHat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import axios from "axios";

import API from "@/utils/api";

// Spicy level indicator component
const SpicyIndicator = ({ level }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3].map((i) => (
        <Flame
          key={i}
          className={`w-3 h-3 ${i <= level ? "text-red-500" : "text-gray-300"}`}
          fill={i <= level ? "currentColor" : "none"}
        />
      ))}
    </div>
  );
};

// Default ingredients structure
const DEFAULT_INGREDIENTS = {
  tortilla: [
    { id: "maiz", name: "Tortilla de Maíz", price: 0, default: true },
    { id: "harina", name: "Tortilla de Harina", price: 0 },
    { id: "integral", name: "Tortilla Integral", price: 0.30 }
  ],
  proteina: [
    { id: "carne_asada", name: "Carne Asada", price: 0, icon: "🥩" },
    { id: "pollo", name: "Pollo al Pastor", price: 0, icon: "🍗" },
    { id: "carnitas", name: "Carnitas", price: 0, icon: "🐷" },
    { id: "barbacoa", name: "Barbacoa", price: 0.50, icon: "🥓" },
    { id: "chorizo", name: "Chorizo", price: 0, icon: "🌭" },
    { id: "vegetariano", name: "Vegetariano (Frijoles)", price: 0, icon: "🫘" }
  ],
  toppings: [
    { id: "cilantro", name: "Cilantro", price: 0 },
    { id: "cebolla", name: "Cebolla", price: 0 },
    { id: "pico_gallo", name: "Pico de Gallo", price: 0 },
    { id: "guacamole", name: "Guacamole", price: 0.75 },
    { id: "queso", name: "Queso Rallado", price: 0.50 },
    { id: "crema", name: "Crema Agria", price: 0.30 },
    { id: "jalapenos", name: "Jalapeños", price: 0 },
    { id: "limon", name: "Limón", price: 0 }
  ],
  salsas: [
    { id: "verde", name: "Salsa Verde", price: 0, spicy: 1 },
    { id: "roja", name: "Salsa Roja", price: 0, spicy: 2 },
    { id: "habanero", name: "Salsa Habanero", price: 0, spicy: 3 },
    { id: "chipotle", name: "Salsa Chipotle", price: 0, spicy: 2 },
    { id: "sin_salsa", name: "Sin Salsa", price: 0, spicy: 0 }
  ]
};

export default function CustomTacoPage() {
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [config, setConfig] = useState(null);

  // Selection states
  const [selectedTortilla, setSelectedTortilla] = useState(DEFAULT_INGREDIENTS.tortilla[0]);
  const [selectedProteina, setSelectedProteina] = useState(null);
  const [selectedToppings, setSelectedToppings] = useState([]);
  const [selectedSalsa, setSelectedSalsa] = useState(DEFAULT_INGREDIENTS.salsas[0]);

  // Base price for custom taco
  const BASE_PRICE = 3.50;

  // Fetch config for potential ingredient overrides
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Get branch context
        const searchParams = new URLSearchParams(window.location.search);
        const urlBranch = searchParams.get("sucursal");
        const savedBranch = localStorage.getItem("app_qr_branch");
        const branchId = urlBranch || savedBranch;
        const branchQuery = branchId ? `?branch_id=${branchId}` : "";

        const response = await axios.get(`${API}/custom-taco/config${branchQuery}`);
        if (response.data) {
          setConfig(response.data);
        }
      } catch (error) {
        console.error("Error fetching config:", error);
      }
    };
    fetchConfig();
  }, []);

  const handleToppingToggle = (topping) => {
    setSelectedToppings((prev) => {
      const exists = prev.find((t) => t.id === topping.id);
      if (exists) {
        return prev.filter((t) => t.id !== topping.id);
      }
      return [...prev, topping];
    });
  };

  const calculateTotal = () => {
    let total = BASE_PRICE;

    // Add tortilla price
    if (selectedTortilla) {
      total += selectedTortilla.price || 0;
    }

    // Add proteina price
    if (selectedProteina) {
      total += selectedProteina.price || 0;
    }

    // Add toppings prices
    total += selectedToppings.reduce((sum, t) => sum + (t.price || 0), 0);

    // Add salsa price (usually 0)
    if (selectedSalsa) {
      total += selectedSalsa.price || 0;
    }

    return total * quantity;
  };

  const buildTacoDescription = () => {
    const parts = [];
    if (selectedTortilla) parts.push(selectedTortilla.name);
    if (selectedProteina) parts.push(selectedProteina.name);
    if (selectedToppings.length > 0) {
      parts.push(selectedToppings.map(t => t.name).join(", "));
    }
    if (selectedSalsa && selectedSalsa.id !== "sin_salsa") {
      parts.push(selectedSalsa.name);
    }
    return parts.join(" + ");
  };

  const handleAddToCart = () => {
    if (!selectedProteina) {
      toast.error("Selecciona una proteína para tu taco");
      return;
    }

    // Build extras array with selected ingredients
    const extras = [];

    if (selectedTortilla && selectedTortilla.price > 0) {
      extras.push({ name: selectedTortilla.name, price: selectedTortilla.price });
    }
    if (selectedProteina && selectedProteina.price > 0) {
      extras.push({ name: selectedProteina.name, price: selectedProteina.price });
    }
    selectedToppings.forEach(topping => {
      if (topping.price > 0) {
        extras.push({ name: topping.name, price: topping.price });
      }
    });

    const tacoItem = {
      item_id: `custom_taco_${Date.now()}`,
      name: "🌮 Taco Personalizado",
      price: BASE_PRICE,
      image: "https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?w=400",
      quantity,
      extras,
      notes: `${buildTacoDescription()}${notes ? ` | ${notes}` : ""}`,
    };

    addItem(tacoItem);

    toast.success("¡Taco personalizado añadido!", {
      description: `${quantity}x ${buildTacoDescription().substring(0, 40)}...`,
    });

    navigate("/menu");
  };

  return (
    <div className="min-h-screen bg-black pb-32">
      {/* Header */}
      <div className="relative h-48 bg-gradient-to-br from-primary via-primary/80 to-primary/60" style={{ background: 'linear-gradient(to bottom right, var(--primary), var(--primary)cc, var(--primary)99)' }}>
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
          <ChefHat className="w-12 h-12 mb-2" />
          <h1 className="font-heading text-3xl font-bold">CREA TU TACO</h1>
          <p className="text-white/80 mt-1">Personaliza cada ingrediente</p>
        </div>

        {/* Back Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 left-4 bg-white/20 backdrop-blur rounded-full text-white hover:bg-white/30"
          onClick={() => navigate("/menu")}
          data-testid="custom-taco-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <Badge className="absolute top-4 right-4 bg-primary text-black font-bold" style={{ backgroundColor: 'var(--primary)' }}>
          Desde {BASE_PRICE.toFixed(2)} €
        </Badge>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6">

        {/* Step 1: Tortilla */}
        <section>
          <h3 className="font-heading font-bold text-lg mb-3 text-primary flex items-center gap-2" style={{ color: 'var(--primary)' }}>
            <span className="w-6 h-6 rounded-full bg-primary text-black text-sm flex items-center justify-center font-bold" style={{ backgroundColor: 'var(--primary)' }}>1</span>
            ELIGE TU TORTILLA
          </h3>
          <div className="grid grid-cols-1 gap-2">
            {DEFAULT_INGREDIENTS.tortilla.map((tortilla) => {
              const isSelected = selectedTortilla?.id === tortilla.id;
              return (
                <button
                  key={tortilla.id}
                  onClick={() => setSelectedTortilla(tortilla)}
                  data-testid={`tortilla-${tortilla.id}`}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isSelected
                    ? "border-primary bg-primary/10"
                    : "border-gray-700 bg-gray-900 hover:border-gray-600"
                    }`}
                  style={isSelected ? { borderColor: 'var(--primary)', backgroundColor: 'rgba(var(--primary-rgb), 0.1)' } : {}}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? "border-primary bg-primary" : "border-gray-500"
                        }`}
                      style={isSelected ? { borderColor: 'var(--primary)', backgroundColor: 'var(--primary)' } : {}}
                    >
                      {isSelected && <Check className="w-3 h-3 text-black" />}
                    </div>
                    <span className="font-medium text-white">{tortilla.name}</span>
                  </div>
                  <span className={`font-bold ${tortilla.price > 0 ? "text-primary" : "text-gray-500"}`} style={tortilla.price > 0 ? { color: 'var(--primary)' } : {}}>
                    {tortilla.price > 0 ? `+${tortilla.price.toFixed(2)} €` : "Incluido"}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Step 2: Proteína */}
        <section>
          <h3 className="font-heading font-bold text-lg mb-3 text-primary flex items-center gap-2" style={{ color: 'var(--primary)' }}>
            <span className="w-6 h-6 rounded-full bg-primary text-black text-sm flex items-center justify-center font-bold" style={{ backgroundColor: 'var(--primary)' }}>2</span>
            ELIGE TU PROTEÍNA *
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {DEFAULT_INGREDIENTS.proteina.map((proteina) => {
              const isSelected = selectedProteina?.id === proteina.id;
              return (
                <button
                  key={proteina.id}
                  onClick={() => setSelectedProteina(proteina)}
                  data-testid={`proteina-${proteina.id}`}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${isSelected
                    ? "border-primary bg-primary/10"
                    : "border-gray-700 bg-gray-900 hover:border-gray-600"
                    }`}
                  style={isSelected ? { borderColor: 'var(--primary)', backgroundColor: 'rgba(var(--primary-rgb), 0.1)' } : {}}
                >
                  <span className="text-2xl mb-1">{proteina.icon}</span>
                  <span className="font-medium text-white text-sm text-center">{proteina.name}</span>
                  <span className={`text-xs mt-1 ${proteina.price > 0 ? "text-primary" : "text-gray-500"}`} style={proteina.price > 0 ? { color: 'var(--primary)' } : {}}>
                    {proteina.price > 0 ? `+${proteina.price.toFixed(2)} €` : "Incluido"}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Step 3: Toppings */}
        <section>
          <h3 className="font-heading font-bold text-lg mb-3 text-primary flex items-center gap-2" style={{ color: 'var(--primary)' }}>
            <span className="w-6 h-6 rounded-full bg-primary text-black text-sm flex items-center justify-center font-bold" style={{ backgroundColor: 'var(--primary)' }}>3</span>
            AÑADE TOPPINGS
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {DEFAULT_INGREDIENTS.toppings.map((topping) => {
              const isSelected = selectedToppings.find(t => t.id === topping.id);
              return (
                <button
                  key={topping.id}
                  onClick={() => handleToppingToggle(topping)}
                  data-testid={`topping-${topping.id}`}
                  className={`flex items-center justify-between p-3 rounded-xl border-2 transition-all ${isSelected
                    ? "border-green-500 bg-green-500/10"
                    : "border-gray-700 bg-gray-900 hover:border-gray-600"
                    }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? "border-green-500 bg-green-500" : "border-gray-500"
                        }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-black" />}
                    </div>
                    <span className="font-medium text-white text-sm">{topping.name}</span>
                  </div>
                  {topping.price > 0 && (
                    <span className="text-primary text-xs font-bold" style={{ color: 'var(--primary)' }}>+{topping.price.toFixed(2)}€</span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Step 4: Salsa */}
        <section>
          <h3 className="font-heading font-bold text-lg mb-3 text-primary flex items-center gap-2" style={{ color: 'var(--primary)' }}>
            <span className="w-6 h-6 rounded-full bg-primary text-black text-sm flex items-center justify-center font-bold" style={{ backgroundColor: 'var(--primary)' }}>4</span>
            ELIGE TU SALSA
          </h3>
          <div className="grid grid-cols-1 gap-2">
            {DEFAULT_INGREDIENTS.salsas.map((salsa) => {
              const isSelected = selectedSalsa?.id === salsa.id;
              return (
                <button
                  key={salsa.id}
                  onClick={() => setSelectedSalsa(salsa)}
                  data-testid={`salsa-${salsa.id}`}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isSelected
                    ? "border-red-500 bg-red-500/10"
                    : "border-gray-700 bg-gray-900 hover:border-gray-600"
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? "border-red-500 bg-red-500" : "border-gray-500"
                        }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="font-medium text-white">{salsa.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {salsa.spicy > 0 && <SpicyIndicator level={salsa.spicy} />}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Notes */}
        <section>
          <h3 className="font-heading font-bold text-lg mb-3 text-white">
            INSTRUCCIONES ESPECIALES
          </h3>
          <Textarea
            placeholder="¿Alguna preferencia adicional? (extra picante, sin sal, etc.)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="custom-taco-notes"
            className="rounded-xl resize-none bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-primary/50"
            style={{ '--tw-ring-color': 'var(--primary)' }}
            rows={2}
          />
        </section>

        {/* Summary */}
        <section className="bg-gray-900 rounded-xl p-4 border border-gray-700">
          <h3 className="font-heading font-bold text-lg mb-2 text-primary" style={{ color: 'var(--primary)' }}>TU TACO</h3>
          <p className="text-gray-400 text-sm">
            {buildTacoDescription() || "Selecciona los ingredientes..."}
          </p>
        </section>
      </div>

      {/* Sticky Add to Cart */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur border-t border-gray-800 p-4 z-40">
        <div className="flex items-center gap-4">
          {/* Quantity Selector */}
          <div className="flex items-center gap-2 bg-gray-800 rounded-full p-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full text-white hover:bg-gray-700"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              data-testid="custom-taco-qty-minus"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="w-8 text-center font-bold text-white" data-testid="custom-taco-qty">
              {quantity}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full text-white hover:bg-gray-700"
              onClick={() => setQuantity(quantity + 1)}
              data-testid="custom-taco-qty-plus"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Add Button */}
          <Button
            className="flex-1 h-12 rounded-full font-bold text-base gap-2 bg-primary hover:opacity-90 text-black"
            style={{ backgroundColor: 'var(--primary)' }}
            onClick={handleAddToCart}
            disabled={!selectedProteina}
            data-testid="custom-taco-add-to-cart"
          >
            <ShoppingBag className="w-5 h-5" />
            Añadir · {calculateTotal().toFixed(2)} €
          </Button>
        </div>
      </div>
    </div>
  );
}
